"use client";

import React, {useCallback, useEffect, useState} from "react"
import Image from "next/image";
import {Locale} from "@/i18n-config";
import type {QuoteSlider as QuoteSliderProps} from "@docid/types/src/payload-types";
import {EmblaCarouselType, EmblaOptionsType} from "embla-carousel";
import Autoplay, {AutoplayOptionsType} from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import {GoDot, GoDotFill} from "react-icons/go";
import {ImQuotesLeft, ImQuotesRight} from "react-icons/im";
import {getMediaBySize, isMedia} from "@/utils/content/media";
import {InlineText} from "@/components/content/common/InlineText";

type Props = {
    lang?: Locale;
    dictionary?: any;
    className?: string;
    block: QuoteSliderProps;
    position?: number;
}

export function QuoteSlider({ lang, dictionary, className, block, position } : Props) {

    const options: EmblaOptionsType = {
        loop: true,
        align: "center",
    }

    const optionsAutoplay: AutoplayOptionsType = {
        playOnInit: true,
        delay: 6000,
    }

    const [emblaRef, emblaApi] = useEmblaCarousel(options, [
        Autoplay(optionsAutoplay)
    ]);

    const {selectedIndex, scrollSnaps, onDotButtonClick} = useDotButton(emblaApi);

    return (
        <div className={className}>
            {block.title && <h2 className="font-bold text-2xl md:text-3xl leading-tight text-zinc-900 text-center md:text-left"><InlineText value={block.title} /></h2>}
            {block.description && <div className="font-source text-xl md:text-xl py-2 text-center md:text-left"><InlineText value={block.description} /></div>}
            <div className="relative">
                <div className="absolute top-10 z-9999 w-full flex justify-center items-center">
                    <div className="flex flex-row justify-center items-center text-zinc-900 bg-white/40 py-1 px-2 rounded-full">
                        {scrollSnaps.map((_, index) => (
                            index === selectedIndex ? (
                                <button key={`dot-${index}`} onClick={() => onDotButtonClick(index)}><GoDotFill size={30}/></button>
                            ) : (
                                <button key={`dot-${index}`} onClick={() => onDotButtonClick(index)}><GoDot size={30}/></button>
                            )
                        ))}
                    </div>
                </div>
                <div ref={emblaRef} className="mt-6 overflow-hidden">
                    <div className="flex backface-hidden touch-pan-y touch-pinch-zoom -ml-5 sm:-ml-10">
                        {block.quotes && block.quotes.map((quote, index) => (
                            <div key={`${quote.id}-${index}`} className="shrink-0 flex-[0_0_100%] max-w-full pl-5 sm:pl-10 text-center cursor-grab">
                                <div
                                    className="bg-white/20 rounded-lg px-5 pt-28 pb-12 md:px-14 md:pb-20 flex flex-col md:flex-row gap-6 md:gap-10 items-center justify-center">
                                    <div className="flex flex-col gap-4">
                                        {quote.image && isMedia(quote.image) && (
                                            <div
                                                className="rounded-full overflow-hidden w-full max-w-[240px] max-h-[240px] md:w-[240px] md:h-[240px]">
                                                {(() => {
                                                    const image = getMediaBySize(quote.image, "small");
                                                    return (
                                                        <Image
                                                            loading="eager"
                                                            style={{objectFit: "cover"}}
                                                            width={image.width}
                                                            height={image.height}
                                                            src={image.url}
                                                            alt={image.alt}
                                                            priority={!!(position && position <= 2)}
                                                        />
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col md:items-start gap-6">
                                        {quote.quote && (
                                            <div className="relative text-white text-center md:text-left pl-14 pr-14">
                                                <ImQuotesLeft className="absolute top-0 left-0" size={40}/>
                                                <p className="relative inline text-xl leading-tight tracking-tight lg:text-3xl lg:leading-tight lg:tracking-tight font-semibold text-left md:text-left text-white select-none">
                                                    <InlineText value={quote.quote} />
                                                </p>
                                                <ImQuotesRight className="absolute bottom-0 right-0" size={40}/>
                                            </div>
                                        )}
                                        <div
                                            className="flex flex-col md:items-start text-lg md:text-xl font-source font-normal text-center md:text-left px-14 leading-snug">
                                            {quote.name &&
                                                <p className="font-semibold text-zinc-900 select-none"><InlineText value={quote.name} /></p>}
                                            {quote.description &&
                                                <p className="text-white select-none"><InlineText value={quote.description} /></p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export type UseDotButtonType = {
    selectedIndex: number
    scrollSnaps: number[]
    onDotButtonClick: (index: number) => void
}

export const useDotButton = (
    emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

    const onDotButtonClick = useCallback(
        (index: number) => {
            if (!emblaApi) return
            emblaApi.scrollTo(index)
        },
        [emblaApi]
    )

    const onInit = useCallback((emblaApi: EmblaCarouselType) => {
        setScrollSnaps(emblaApi.scrollSnapList())
    }, [])

    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setSelectedIndex(emblaApi.selectedScrollSnap())
    }, [])

    useEffect(() => {
        if (!emblaApi) return
        onInit(emblaApi)
        onSelect(emblaApi)
        emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect)
    }, [emblaApi, onInit, onSelect])

    return {
        selectedIndex,
        scrollSnaps,
        onDotButtonClick
    }

}
